import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Drawer, Field } from "@/components/ui-kit";
import { toast } from "sonner";
import { Copy } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

function CopyRow({ label, value, testId }) {
  const copy = () => {
    navigator.clipboard.writeText(value || "");
    toast.success("Copied");
  };
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</label>
      <div className="mt-1 flex items-center gap-2 font-mono text-sm bg-white border border-slate-200 rounded-md px-3 py-2">
        <div className="flex-1 truncate" data-testid={testId}>{value}</div>
        <button onClick={copy} className="text-xs text-slate-600 hover:underline flex items-center gap-1 flex-shrink-0">
          <Copy size={12} /> Copy
        </button>
      </div>
    </div>
  );
}

// Shared drawer for creating a prospect demo sandbox, used from both the Prospects page
// (prospect prefill + linked prospect_id) and the Tenants page (blank, no prospect link).
export default function DemoTenantDrawer({ open, onClose, prospect, onCreated }) {
  const [form, setForm] = useState({ name: "", admin_email: "", days: 14, with_docs: true });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCreated(null);
    setSaving(false);
    const fallbackName = prospect?.name ? `${prospect.name} demo` : "";
    setForm({
      name: prospect?.company || fallbackName,
      admin_email: prospect?.email || "",
      days: 14,
      with_docs: true,
    });
  }, [open, prospect]);

  const create = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name,
        admin_email: form.admin_email,
        days: Number(form.days) || 14,
        with_docs: !!form.with_docs,
      };
      if (prospect?.id) body.prospect_id = prospect.id;
      const { data } = await api.post("/platform/demo-tenants", body);
      setCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const finishUp = () => {
    setCreated(null);
    onClose();
    if (onCreated) onCreated();
  };

  return (
    <Drawer
      open={open}
      onClose={created ? finishUp : onClose}
      title={created ? "Sandbox created" : "Create demo sandbox"}
      footer={
        created ? (
          <button onClick={finishUp} className="btn-primary" data-testid="demo-tenant-done">
            Done
          </button>
        ) : (
          <>
            <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-slate-200">
              Cancel
            </button>
            <button onClick={create} disabled={saving} className="btn-primary" data-testid="prospect-demo-create">
              Create sandbox
            </button>
          </>
        )
      }
    >
      {created ? (
        <div className="space-y-4">
          <div className="text-sm text-slate-600">
            Write these down or send them now, the password is not shown again. Data finishes loading within a minute.
          </div>
          <CopyRow label="Login URL" value={created.login_url} testId="demo-tenant-login-url" />
          <CopyRow label="Email" value={created.admin_email} testId="demo-tenant-email-value" />
          <CopyRow label="Password" value={created.password} testId="demo-tenant-password" />
          <div className="text-xs text-slate-500">Expires {fmtDate(created.expires_at)}</div>
        </div>
      ) : (
        <div className="space-y-4">
          <Field
            label="Agency name"
            testId="demo-tenant-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Field
            label="Admin email"
            testId="demo-tenant-admin-email"
            type="email"
            value={form.admin_email}
            onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
          />
          <Field
            label="Days"
            testId="demo-tenant-days"
            type="number"
            min="1"
            max="60"
            value={form.days}
            onChange={(e) => setForm({ ...form, days: e.target.value })}
          />
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!form.with_docs}
              onChange={(e) => setForm({ ...form, with_docs: e.target.checked })}
              data-testid="demo-tenant-with-docs"
              className="mt-0.5"
            />
            <span>
              Include sample driver documents
              <div className="text-xs text-slate-500">Slower to create, shows the compliance vault.</div>
            </span>
          </label>
        </div>
      )}
    </Drawer>
  );
}
